/**
 * Staff create a report by binding each of its kind's slots to an object of the project.
 *
 * The backend checks the same rules again (RP3); this only keeps the form from offering an
 * object that could not be bound: the wrong kind, not ready, or deleted. Both the points and the
 * images a report shows come from the object's preprocessed data.
 */
import { useState } from 'react';
import { Button, Group, Modal, Select, Stack, Text, TextInput } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useTranslation } from 'react-i18next';
import { errorMessage } from '@/api/errors';
import { useProjectObjects, type DataObject } from '@/api/objects';
import { useCreateReport, useReportKinds, type ReportKind, type ReportKindInfo, type ReportSlot } from '@/api/reports';
import { objectName } from '@/features/objects/objectName';
import { useKindLabel } from '@/features/objects/useKindLabel';
import { brand } from '@/theme/theme';
import { SchemaForm, type JsonSchema } from './options/SchemaForm';
import { optionErrors } from './options/schemaPath';

export interface CreateReportModalProps {
  projectId: string;
  opened: boolean;
  onClose: () => void;
}

function eligible(objects: DataObject[], slot: ReportSlot): DataObject[] {
  return objects.filter((o) => o.kind === slot.kind && o.client_status === 'ready' && !o.deleted);
}

export function CreateReportModal({ projectId, opened, onClose }: CreateReportModalProps) {
  const { t } = useTranslation('reports');
  const kindLabel = useKindLabel();
  const objects = useProjectObjects(projectId);
  const create = useCreateReport(projectId);
  const [kind, setKind] = useState<ReportKind>('orthophoto');
  const [name, setName] = useState('');
  const [inputs, setInputs] = useState<Record<string, string>>({});
  // null until the staff member edits: the kind's defaults show meanwhile, so a kind change needs no reseeding.
  const [edited, setEdited] = useState<Record<string, unknown> | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const kinds = useReportKinds();
  const info: ReportKindInfo | undefined = kinds.data?.find((k) => k.key === kind);
  const slots: ReportSlot[] = info?.slots ?? [];
  const options = edited ?? ((info?.options_defaults ?? {}) as Record<string, unknown>);
  const ready = slots.filter((s) => s.required).every((s) => inputs[s.role]);

  const submit = () => {
    const bound = Object.fromEntries(
      Object.entries(inputs).filter(([role, object]) => !!object && slots.some((s) => s.role === role)),
    );
    void create
      .mutateAsync({ kind, name, inputs: bound, options })
      .then(() => {
        notifications.show({ color: 'forest', message: t('create.created') });
        setInputs({});
        setName('');
        setEdited(null);
        setErrors({});
        onClose();
      })
      .catch((e: unknown) => {
        const byPath = optionErrors(e);
        setErrors(byPath);
        if (!Object.keys(byPath).length) notifications.show({ color: 'red', message: errorMessage(e) });
      });
  };

  return (
    <Modal opened={opened} onClose={onClose} title={t('create.title')} size="lg">
      <Stack gap="md">
        <Select
          label={t('create.kind')}
          data={(kinds.data ?? []).map((k) => ({ value: k.key, label: t(`kinds.${k.key}`) }))}
          value={kind}
          allowDeselect={false}
          onChange={(v) => {
            setKind((v as ReportKind) ?? 'orthophoto');
            setInputs({});
            setEdited(null);
            setErrors({});
          }}
        />
        {slots.map((slot) => {
          const choices = eligible(objects.data ?? [], slot);
          return (
            <Select
              key={slot.role}
              label={`${t(`roles.${slot.role}`, { defaultValue: slot.role })}${slot.required ? '' : ` (${t('create.optional')})`}`}
              description={kindLabel(slot.kind)}
              placeholder={choices.length ? t('create.pick') : t('create.none')}
              disabled={!choices.length}
              data={choices.map((o) => ({ value: o.public_id, label: objectName(o) }))}
              value={inputs[slot.role] ?? null}
              clearable={!slot.required}
              onChange={(v) => setInputs((prev) => ({ ...prev, [slot.role]: v ?? '' }))}
            />
          );
        })}
        <TextInput label={t('create.name')} value={name} onChange={(e) => setName(e.currentTarget.value)} />
        {info && Object.keys((info.options_schema as JsonSchema).properties ?? {}).length > 0 && (
          <SchemaForm kind={kind} schema={info.options_schema as JsonSchema} value={options} onChange={setEdited} errors={errors} />
        )}
        <Text fz={12} c={brand.textMuted}>
          {t('share.hint')}
        </Text>
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>
            {t('share.cancel')}
          </Button>
          <Button onClick={submit} disabled={!ready} loading={create.isPending}>
            {t('create.submit')}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
